import type { CustomMetric, XAxis } from '@neptune/shared/core-apis-leaderboard-domain';

export type FloatSeriesExpression = {
  formula: string;
  attributes: string[];
  runAttributes?: string[];
  /** Defaults of runAttributes by index; null when an attribute has no default. */
  runAttributeDefaults?: (number | null)[];
};

export function expressionXAxis(expression: FloatSeriesExpression): XAxis {
  const runAttributes = expression.runAttributes ?? [];
  const runAttributeDefaults = expression.runAttributeDefaults ?? [];

  return {
    custom: {
      attributes: [...expression.attributes],
      customXFormula: expression.formula,
      ...(runAttributes.length > 0 ? { runAttributes: [...runAttributes] } : {}),
      ...(runAttributeDefaults.some((value) => value !== null)
        ? { runAttributeDefaults: toApiRunAttributeDefaults(runAttributeDefaults) }
        : {}),
    },
  };
}

/**
 * The API reads a null entry as "no default", but the generated client types the entries as numbers,
 * because the Swagger 2.0 spec of the API cannot mark array items as nullable.
 */
export function toApiRunAttributeDefaults(runAttributeDefaults: (number | null)[]): number[] {
  return [...runAttributeDefaults] as number[];
}

export function attributeXAxis(name: string): XAxis {
  return { custom: { attribute: name } };
}

export function standardXAxis(kind: 'step' | 'relativeTime' | 'absoluteTime'): XAxis {
  switch (kind) {
    case 'step':
      return { steps: {} };
    case 'relativeTime':
      return { relativeMillis: {} };
    case 'absoluteTime':
      return { epochMillis: {} };
  }
}

export function expressionMetric({
  expression,
  ...metric
}: Omit<CustomMetric, 'attributes' | 'customYFormula'> & {
  expression: FloatSeriesExpression;
}): CustomMetric {
  return { ...metric, attributes: [...expression.attributes], customYFormula: expression.formula };
}

/** Chart displays a bucket mean, not its representative point; zero finite values is a gap. */
export function selectChartDisplayValue(point: {
  y?: number | null;
  bucket?: { sum: number | null; count: number } | null;
}): number {
  if (point.bucket?.count === 0) {
    return Infinity;
  }

  const mean =
    point.bucket && point.bucket.count ? (point.bucket.sum ?? NaN) / point.bucket.count : undefined;
  return mean ?? point.y ?? NaN;
}
